import { BringgFontIcons } from '@bringg/bringg-icons';
import { ProgressBar } from '@bringg-frontend/progress-bar';
import { dateUtils } from '@bringg-frontend/utils';
import { useTranslation } from 'react-i18next';

import { CapacityUtilization } from '../../../capacity-utilization.types';
import { CapacityUtilizationHeader } from './capacity-utilization-header';
import { UsedTimeIndicator } from './progress-bar-section-time-indicator';

import styles from './progress-bar-section.module.scss';

const PERCENT = 100;
const SECONDS_IN_MINUTE = 60;
const T_PREFIX = 'PLANNED_DELIVERY_WINDOWS_POPOVER_CONTENT.PROGRESS_BAR';

interface ProgressBarSectionProps {
	capacityUtilization: CapacityUtilization;
	className?: string;
}

export const ProgressBarSection = ({ capacityUtilization, className }: ProgressBarSectionProps) => {
	const {
		totalSlotSeconds,
		plannedSeconds,
		reservedSeconds,
		availableSeconds,
		deliveryBlockSeconds,
		totalBreakSeconds,
		totalLoadingTimeMinutes,
		totalUnloadingTimeMinutes
	} = capacityUtilization.fillRatioData.routeDuration;

	const safeReservedSeconds = reservedSeconds ?? 0;
	const usedSum = plannedSeconds + safeReservedSeconds + availableSeconds;
	const isOverbooked = usedSum > totalSlotSeconds;
	const usedWidth = totalSlotSeconds > 0 ? `${(usedSum / totalSlotSeconds) * PERCENT}%` : '0%';
	const { t } = useTranslation();

	return (
		<div className={styles.progressBarSection}>
			<CapacityUtilizationHeader
				iconName={BringgFontIcons.Clock}
				label={t('PLANNED_DELIVERY_WINDOWS_POPOVER_CONTENT.PROGRESS_BAR.ROUTE_DURATION')}
				availableSeconds={availableSeconds}
			/>
			<div className={styles.labelsContainer}>
				<span className={styles.utilizedLabel}>
					{dateUtils.formatDuration(plannedSeconds)} {t(`${T_PREFIX}.PLANNED`)} *
				</span>
				{safeReservedSeconds ? (
					<span className={styles.reservedLabel}>
						+ {dateUtils.formatDuration(safeReservedSeconds)} {t(`${T_PREFIX}.RESERVED`)}
						<span className={styles.totalLabel}>/ {dateUtils.formatDuration(totalSlotSeconds)} </span>
						{t(`${T_PREFIX}.TOTAL_SLOT`)}
					</span>
				) : null}
			</div>
			<div className={styles.progressBarWrapper}>
				<ProgressBar
					totalSlotSeconds={totalSlotSeconds}
					plannedSeconds={plannedSeconds}
					reservedSeconds={reservedSeconds}
					availableSeconds={availableSeconds}
					className={className}
				/>
				<span className={styles.totalSlotTime}>{dateUtils.formatDuration(totalSlotSeconds)}</span>
				<div className={styles.indicator}>
					{!isOverbooked && (
						<div style={{ width: usedWidth }}>
							<UsedTimeIndicator usedWidth="100%" />
							<span className={styles.resourcesDeliveryBlock}>
								{t(`${T_PREFIX}.RESOURCES`)} {dateUtils.formatDuration(deliveryBlockSeconds)}
							</span>
						</div>
					)}
					<span className={styles.plannedRouteIncludes}>
						*{t(`${T_PREFIX}.INCLUDES`)}:{' '}
						{dateUtils.formatDuration(totalLoadingTimeMinutes * SECONDS_IN_MINUTE)}{' '}
						{t(`${T_PREFIX}.LOADING`)} + {dateUtils.formatDuration(totalBreakSeconds)}{' '}
						{t(`${T_PREFIX}.BREAK`)} +{' '}
						{dateUtils.formatDuration(totalUnloadingTimeMinutes * SECONDS_IN_MINUTE)}{' '}
						{t(`${T_PREFIX}.UNLOADING`)}
					</span>
				</div>
			</div>
		</div>
	);
};
